var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Schema, ArraySchema, type } from "@colyseus/schema";
/**
 * A server-authoritative attack NPC (a wild hog mob). Lives in `GameState.hogs`, synced to every
 * client like players/ground items. Position + AI are computed entirely server-side (unlike the
 * client-authoritative player movement); the client just renders the synced fields with a billboard.
 *
 * NOTE: like `state.ts`, this file is compiled with `tsc` (legacy decorators) and must NOT be
 * transformed by esbuild/Vite directly — consumers import the built output in `dist/`.
 */
export class Hog extends Schema {
    constructor() {
        super(...arguments);
        this.x = 0;
        this.y = 0;
        /** Facing as a compass name (one of `HOG_DIRS`); drives the client's directional sprite row. */
        this.dir = "south";
        /** Animation/AI state the client renders: idle | walk | run | charge | attack | dead. */
        this.state = "idle";
        this.health = 60;
        this.maxHealth = 60;
        /** True briefly when the hog acquires a target — drives the "!" alert popup above its head. */
        this.alerted = false;
        /** Stunned (frozen) — briefly true after a player fully blocks its lunge. Drives a stagger shake. */
        this.stunned = false;
    }
}
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Hog.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Hog.prototype, "y", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Hog.prototype, "dir", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Hog.prototype, "state", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Hog.prototype, "health", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Hog.prototype, "maxHealth", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Hog.prototype, "alerted", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Hog.prototype, "stunned", void 0);
/**
 * A wild GOBLIN (combat level 10) — a server-authoritative attack NPC roaming the Kapon wilds. Like
 * the hog, position + AI are computed server-side and mirrored by a billboard client-side. Walks into
 * melee, raises one arm overhead (the `attack` state is the wind-up + swing), and lands its hit
 * mid-swing so a player has time to block.
 */
export class Goblin extends Schema {
    constructor() {
        super(...arguments);
        this.x = 0;
        this.y = 0;
        /** Facing as a compass name (one of `HOG_DIRS`); drives the client's directional sprite row. */
        this.dir = "south";
        /** Animation/AI state the client renders: idle | walk | attack | dead. */
        this.state = "idle";
        this.health = 60;
        this.maxHealth = 60;
        this.alerted = false;
        this.stunned = false;
    }
}
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Goblin.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Goblin.prototype, "y", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Goblin.prototype, "dir", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Goblin.prototype, "state", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Goblin.prototype, "health", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Goblin.prototype, "maxHealth", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Goblin.prototype, "alerted", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Goblin.prototype, "stunned", void 0);
/**
 * A wild SLIME (combat level 15) — a server-authoritative attack NPC. Hops toward a target, then
 * randomly performs one of two attacks: a single-target forward `attack` (lunge body-slam) or a
 * `splash` AoE that damages everyone within a radius (telegraphed by a green charge-up glow).
 */
export class Slime extends Schema {
    constructor() {
        super(...arguments);
        this.x = 0;
        this.y = 0;
        /** Facing as a compass name (one of `HOG_DIRS`); drives the client's directional sprite row. */
        this.dir = "south";
        /** Animation/AI state the client renders: idle | walk | attack | splash | dead. */
        this.state = "idle";
        this.health = 60;
        this.maxHealth = 60;
        this.alerted = false;
        this.stunned = false;
    }
}
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Slime.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Slime.prototype, "y", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Slime.prototype, "dir", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Slime.prototype, "state", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Slime.prototype, "health", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Slime.prototype, "maxHealth", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Slime.prototype, "alerted", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Slime.prototype, "stunned", void 0);
/**
 * A summoned PET hog (mini companion). Spawned by right-clicking the Hog Charm; one per owner, keyed
 * by the owner's sessionId. It follows the owner's position (server-driven) across every scene, plays
 * idle animations when settled, never attacks, and CAN be selected + killed by other players in the
 * wild. On death (or owner disconnect) it's removed; the charm's cooldown gates re-summoning.
 */
export class PetHog extends Schema {
    constructor() {
        super(...arguments);
        this.x = 0;
        this.y = 0;
        /** Facing as a compass name (one of `HOG_DIRS`) — drives the client's directional sprite row. */
        this.dir = "south";
        /** Animation the client renders: "walk" (following), "attack" (engaged in combat), or "idle" (settled). */
        this.state = "idle";
        this.health = 30;
        this.maxHealth = 30;
        /** The owning player's sessionId — the pet follows this player and renders with them everywhere. */
        this.ownerSid = "";
        /** Display name shown when targeted (e.g. "<Owner>'s Hoglet"). */
        this.name = "Hoglet";
        /** Pet level (derived from its charm stats) — drives the sprite size; bigger = higher level. */
        this.level = 1;
    }
}
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PetHog.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PetHog.prototype, "y", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PetHog.prototype, "dir", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PetHog.prototype, "state", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PetHog.prototype, "health", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PetHog.prototype, "maxHealth", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PetHog.prototype, "ownerSid", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PetHog.prototype, "name", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PetHog.prototype, "level", void 0);
/**
 * A friendly gate GUARD — a server-driven combat NPC that holds the Kapon side of the city gate.
 * Like a hog, position + AI are server-authoritative; the client renders the synced fields with a
 * humanoid billboard. Guards leave their post to help a player a hog is chasing, then return.
 */
export class Guard extends Schema {
    constructor() {
        super(...arguments);
        this.x = 0;
        this.y = 0;
        /** Facing as a world heading (radians, atan2(dx,dy)); drives the billboard's directional row. */
        this.heading = 0;
        /** Whether the guard is moving (drives walk vs idle animation). */
        this.moving = false;
        /** Animation/AI state the client renders: idle | walk | attack | dead. */
        this.state = "idle";
        this.health = 140;
        this.maxHealth = 140;
        this.name = "Guard";
    }
}
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Guard.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Guard.prototype, "y", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Guard.prototype, "heading", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Guard.prototype, "moving", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Guard.prototype, "state", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Guard.prototype, "health", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Guard.prototype, "maxHealth", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Guard.prototype, "name", void 0);
/**
 * A server-authoritative CITY NPC — a townsperson the server simulates so every player sees it in the
 * same place. v1 is the Watabaga walk-in CUSTOMER (a humanoid billboard that strolls in, "orders", and
 * leaves). `kind` lets the same entity later cover wandering townsfolk + vendors. Position/heading are
 * server-driven; `look` is an appearance seed the client expands into a deterministic random outfit.
 */
export class CityNpc extends Schema {
    constructor() {
        super(...arguments);
        this.x = 0;
        this.y = 0;
        /** Facing as a world heading (radians, atan2(dx,dy)) — drives the billboard's directional row. */
        this.heading = 0;
        this.moving = false;
        /** What this NPC is: "customer" (v1) | later "wander" | "vendor". */
        this.kind = "customer";
        /** Appearance seed → a deterministic random look on every client (so all players see the same NPC). */
        this.look = 0;
        /** A persistent display name (set once at spawn) shown when you target/talk to this townsperson. */
        this.name = "";
    }
}
__decorate([
    type("number"),
    __metadata("design:type", Object)
], CityNpc.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], CityNpc.prototype, "y", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], CityNpc.prototype, "heading", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], CityNpc.prototype, "moving", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], CityNpc.prototype, "kind", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], CityNpc.prototype, "look", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], CityNpc.prototype, "name", void 0);
/**
 * A loot chest dropped by a slain mob (10% chance from a hog). Renders as an open, glowing chest
 * the player walks up to and opens; clicking an item moves it to their bag (server removes it from
 * `items`). When `items` empties — or its TTL elapses — the server deletes the chest from state.
 */
export class Chest extends Schema {
    constructor() {
        super(...arguments);
        this.x = 0;
        this.y = 0;
        /** Remaining loot itemIds (1-2 at spawn); the client lists these and the server pops them on take. */
        this.items = new ArraySchema();
        /** Parallel to `items`: encoded per-instance rolled mods (e.g. armored leather), "" when none. */
        this.mods = new ArraySchema();
    }
}
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Chest.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Chest.prototype, "y", void 0);
__decorate([
    type(["string"]),
    __metadata("design:type", Object)
], Chest.prototype, "items", void 0);
__decorate([
    type(["string"]),
    __metadata("design:type", Object)
], Chest.prototype, "mods", void 0);
//# sourceMappingURL=mobs.js.map